import { ICompileService } from "angular";
import { IFieldConfigurationObject } from "AngularFormly";
import { ProgressiveDefinitionDetailReadingsViewController } from "../../../../../machines.progressives/views/definitions/detail/readings";
import { RecordDataSource } from "../../../../../records/grids";
import {
  CellDoubleClickedEventHandlerArgs,
  DoubleClickHandlerService,
  PatchFieldConfiguration,
  vsName as DoubleClickHandlerServiceName,
} from "../../../../../records/grids/cells/doubleClickHandlerService";
import { FilterProvider } from "../../../../../records/grids/columns/filters/FilterProvider";
import { OptionsFilter } from "../../../../../records/grids/columns/filters/subFilters/OptionsFilter";
import {
  GridSettingsService,
  vsName as GridSettingsServiceName,
  IGridSettings,
} from "../../../../../records/grids/settings/gridSettingsService";
import {
  moneyValueGetterFactory,
  percentageValueGetterFactory,
} from "../../../../../records/grids/cells/valueGetters";
import { PatchObject } from "../../../../../records/server/crud";
import { IQuery } from "../../../../../records/server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../../records/server/serverUtility";
import {
  ColumnDefinitionService,
  vsName as ColumnDefinitionServiceName,
} from "../../../../../records/grids/columns/columnDefinitionService";
import { ColDef } from "../../../../../records/grids/columns/colDef";
import { ProgressivePermissions } from "../../../../../machines.progressives/permissions";
import { GridArgs } from "../../../../../records/grids/gridArgs";

const apiUrl =
  "/api/machines/progressives/ProgressiveDailyReading/GameLevel/Reading";
const gridId = Symbol("machineDetailProgressiveReadings");

export class MachinesDetailProgressiveMeterReadingsViewController {
  static $inject = [
    GridSettingsServiceName,
    "machine",
    "$compile",
    DoubleClickHandlerServiceName,
    ServerUtilityName,
    ColumnDefinitionServiceName,
  ];

  readonly gridDataSource: RecordDataSource;
  readonly columns: ColDef[];
  readonly gridSettings: IGridSettings;
  readingOfRecordOnly: boolean = true;
  readonly defaultQuery: IQuery;

  constructor(
    gridSettingsService: GridSettingsService,
    machine: any,
    $compile: ICompileService,
    doubleClickHandlerService: DoubleClickHandlerService,
    serverUtility: ServerUtility,
    columnDefinitionService: ColumnDefinitionService
  ) {
    this.gridDataSource = new RecordDataSource(serverUtility, apiUrl);
    this.gridSettings = gridSettingsService.getGridSettings(gridId);
    this.defaultQuery = {
      MachineId: machine.Id,
      ReadingOfRecord: this.readingOfRecordOnly,
    };
    this.columns = [
      {
        field: "Id",
        headerName: "Id",
        filter: new FilterProvider("Id", $compile)
          .defaultNumberFilters()
          .getGridFilterFactory(),
        hide: true,
      },
      {
        field: "MeterReading",
        headerName: "Meter Reading",
        valueGetter: (params: { data: any; colDef: ColDef }) => {
          const data = <any>params.data;
          return ProgressiveDefinitionDetailReadingsViewController.meterReadingFormatter(
            {
              cellValue: data[params.colDef!.field!],
              row: params.data,
            }
          );
        },
        filter: new FilterProvider("LastMeterReading", $compile)
          .defaultNumberFilters()
          .addFilter(
            "Unable To Get Reading",
            new OptionsFilter({
              displayName: "Unable To Get Reading",
              options: [
                { display: "Yes", value: true },
                { display: "No", value: false },
              ],
              queryFieldNames: ["LastUnableToGetReading"],
            })
          )
          .addFilter(
            "Condition Not Met",
            new OptionsFilter({
              displayName: "Condition Not Met",
              options: [
                { display: "Yes", value: true },
                { display: "No", value: false },
              ],
              queryFieldNames: ["LastConditionNotMet"],
            })
          )
          .getGridFilterFactory(),
        cellDoubleClicked: doubleClickHandlerService.patchDoubleClickHandler({
          requiresPermission: ProgressivePermissions.EditPermission,
          fetchRecord: (args) =>
            serverUtility.querySingle({
              url: apiUrl,
              query: { Id: args.data.Id },
            }),
          submit: (updateObject: PatchObject) =>
            serverUtility.put({ url: apiUrl, updateObject }),
          patchField: (args) => this.getMeterReadingFields(args),
          getInitialPatchObject: (args) => {
            return {
              Id: args.data.Id,
              MeterReading: args.data.MeterReading,
              ConditionNotMet: args.data.ConditionNotMet,
              UnableToGetReading: args.data.UnableToGetReading,
            };
          },
          getRecordName: (args) => {
            const {
              ProgressiveDefinitionName: progressiveDefinition,
              ProgressiveDefinitionGameDescription: game,
              ProgressiveDefinitionGameLevelName: level,
              ProgressiveDailyMachineLocation: location,
            } = args.data;

            return `level reading ${progressiveDefinition}/${game}/${level} at ${location}`;
          },
        }),
      },
      columnDefinitionService.getDateColDef({
        field: "ProgressiveDailyPeriodDate",
        header: "Date",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionName",
        header: "Progressive",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveMeterReadingSettingName",
        header: "Meter Reading Setting",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionGameDescription",
        header: "Game",
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDefinitionGameOrder",
        header: "Game Order",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDefinitionGameLevelName",
        header: "Level",
      }),
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDefinitionGameLevelOrder",
        header: "Level Order",
        hide: true,
      }),
      {
        field: "ProgressiveDefinitionGameLevelJackpotType",
        headerName: "Jackpot Type",
        filter: new FilterProvider(
          "ProgressiveDefinitionGameLevelJackpotType",
          $compile
        )
          .addFilter(
            "=",
            new OptionsFilter({
              displayName: "=",
              queryFieldNames: ["ProgressiveDefinitionGameLevelJackpotType"],
              options: [
                {
                  display: "Linked",
                  value: "Linked",
                },
                {
                  display: "Standalone",
                  value: "Standalone",
                },
              ],
              includeNullValue: true,
            })
          )
          .getGridFilterFactory(),
      },
      columnDefinitionService.getNumberColDef({
        field: "ProgressiveDefinitionGameLevelResetBase",
        header: "Reset Base",
        valueGetter: moneyValueGetterFactory(),
      }),
      columnDefinitionService.getBooleanColDef({
        field: "ReadingOfRecord",
        suppressSorting: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineManufacturerName",
        header: "Machine Manufacturer",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSerialNumber",
        header: "Machine Serial Number",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineLocation",
        header: "Machine Location",
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSection",
        header: "Machine Section",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineBank",
        header: "Machine Bank",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ProgressiveDailyMachineSlot",
        header: "Machine Slot",
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "LastKnownMeterReading",
        header: "Last Period Reading",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "LastKnownReadingUser",
        header: "Last Period Reading User",
        hide: true,
      }),
      columnDefinitionService.getDateTimeColDef({
        field: "LastKnownReadingTime",
        header: "Last Period Reading Time",
        hide: true,
      }),

      columnDefinitionService.getNumberColDef({
        field: "ChangePercentage",
        header: "Change Percentage",
        valueGetter: percentageValueGetterFactory({ decimalPlaces: 2 }),
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "Change",
        header: "Change",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getNumberColDef({
        field: "Liability",
        header: "Liability",
        valueGetter: moneyValueGetterFactory(),
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "CreatedByFirstName",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "CreatedByLastName",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "CreatedByDisplayName",
        header: "Created By",
      }),
      columnDefinitionService.getDateTimeColDef({ field: "CreatedDate" }),
      columnDefinitionService.getStringColDef({
        field: "ModifiedByFirstName",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ModifiedByLastName",
        hide: true,
      }),
      columnDefinitionService.getStringColDef({
        field: "ModifiedByDisplayName",
        header: "Modified By",
      }),
      columnDefinitionService.getDateTimeColDef({ field: "ModifiedDate" }),
    ];
  }

  readingOfRecordOnlyChanged(readingOfRecordOnly: boolean) {
    if (readingOfRecordOnly) {
      this.defaultQuery.ReadingOfRecord = true;
    } else {
      this.defaultQuery.ReadingOfRecord = undefined;
    }
  }

  private getMeterReadingFields(
    args: CellDoubleClickedEventHandlerArgs
  ): PatchFieldConfiguration[] {
    const meterReadingField: IFieldConfigurationObject = {
      key: "MeterReading",
      name: "MeterReading",
      templateOptions: {
        label: "Meter Reading",
        min: args.data.ProgressiveDefinitionGameLevelResetBase,
        type: "number",
      },
      type: "gridInput",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.ConditionNotMet = false;
            model.UnableToGetReading = false;
          }
        },
      },
    };

    const conditionNotMetField: IFieldConfigurationObject = {
      key: "ConditionNotMet",
      name: "ConditionNotMet",
      templateOptions: {
        label: "Condition Not Met",
      },
      type: "gridCheckbox",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.MeterReading = null;
            model.UnableToGetReading = false;
          }
        },
      },
    };

    const unableToGetReadingField: IFieldConfigurationObject = {
      key: "UnableToGetReading",
      name: "UnableToGetReading",
      templateOptions: {
        label: "Unable To Get Reading",
      },
      type: "gridCheckbox",
      watcher: {
        listener: (field, newValue, oldValue, scope) => {
          if (newValue) {
            const model = scope.model!;
            model.MeterReading = null;
            model.ConditionNotMet = false;
          }
        },
      },
    };

    let fields: PatchFieldConfiguration[] = [
      {
        formField: meterReadingField,
        referenceFieldFormatters: [
          (data) =>
            Number.isFinite(data) ? `$${(<number>data).toFixed(2)}` : undefined,
        ],
      },
      {
        formField: unableToGetReadingField,
        referenceFieldFormatters: [(data) => (data ? "Yes" : "No")],
      },
    ];

    const data = args.data;

    if (
      data.OptionalBelow &&
      data.ProgressiveDefinitionGameLevelResetBase &&
      data.OptionalBelow >= data.ProgressiveDefinitionGameLevelResetBase
    ) {
      fields = [
        ...fields,
        {
          formField: conditionNotMetField,
          referenceFieldFormatters: [(data) => (data ? "Yes" : "No")],
        },
      ];
    }

    return fields;
  }
}
